// 微電網 mock 資料
//
// 廠商 API 尚未串接，先以此產生前端所需的欄位。輸出格式與 store 的 historyData
// 一致（欄位導向：每個欄位一個陣列，索引與 DateTime 對齊），所以真實資料進來時
// 只需在 fetchHistoryData 內把這個函式換掉，其餘程式碼零改動。
//
// 資料造型依實測觀察：太陽能白天拱形、負載全日波動、市電補差額、
// 電池 SOC 幾乎持平、充放電恆為 0（電池目前未作用）。

// 以時間戳當種子的偽亂數，同一個時間點永遠得到同一個值。
// 用 Math.random() 會讓每次重新渲染的圖形都在跳動。
function seededNoise(timestamp, salt) {
    const x = Math.sin(timestamp / 60000 + salt * 999) * 10000
    return x - Math.floor(x) // 0 ~ 1
}

// 瞬時功率欄位的單位是 W（非 kW）；只有 todaypvpower / todayloadpower 是 kWh。
const SUNRISE = 6
const SUNSET = 18
const PV_PEAK_W = 3200

// 太陽能：日出到日落之間的鐘形曲線，乘上雲量擾動
function pvAt(date, timestamp) {
    const hour = date.getHours() + date.getMinutes() / 60
    if (hour <= SUNRISE || hour >= SUNSET) return 0

    const arc = Math.sin((Math.PI * (hour - SUNRISE)) / (SUNSET - SUNRISE))
    const cloud = 0.72 + seededNoise(timestamp, 1) * 0.33
    return PV_PEAK_W * Math.pow(arc, 1.3) * cloud
}

// 負載：夜間低、日間高，疊上明顯波動
function loadAt(date, timestamp) {
    const hour = date.getHours() + date.getMinutes() / 60
    const daytime = hour >= 7 && hour <= 21 ? 1100 : 0
    const base = 1150 + daytime
    const swing = (seededNoise(timestamp, 2) - 0.5) * 1400
    return Math.max(400, base + swing)
}

// 電池 SOC：幾乎不動，只有極小漂移
function socAt(timestamp) {
    return 52 + (seededNoise(timestamp, 3) - 0.5) * 0.8
}

/**
 * 依既有的 DateTime 陣列產生對齊的微電網欄位。
 *
 * @param {Date[]} dateTimeList 由 fetchHistoryData 整理好的時間序列（已由舊到新）
 * @returns {Object} 欄位導向的資料，每個陣列長度與 dateTimeList 相同
 */
export function buildMicrogridColumns(dateTimeList) {
    const columns = {
        pvpower: [],
        gridpower: [],
        loadmeteractivepower: [],
        batterysoc: [],
        chargingpower: [],
        dischargingpower: [],
        todaypvpower: [],
        todayloadpower: [],
    }

    if (!Array.isArray(dateTimeList) || dateTimeList.length === 0) return columns

    // 累積量以「當日」為單位歸零；資料視窗可能跨午夜，故偵測日期變化
    let currentDay = null
    let pvAccum = 0
    let loadAccum = 0
    let prevTime = null

    dateTimeList.forEach((dateTime) => {
        const date = dateTime instanceof Date ? dateTime : new Date(dateTime)
        const timestamp = date.getTime()
        const dayKey = date.toDateString()

        if (dayKey !== currentDay) {
            currentDay = dayKey
            pvAccum = 0
            loadAccum = 0
            prevTime = null
        }

        const pv = pvAt(date, timestamp)
        const load = loadAt(date, timestamp)
        // 市電補上負載與太陽能的差額；太陽能過剩時視為 0（不考慮躉售回饋）
        const grid = Math.max(0, load - pv)

        // 功率是 W，累積量要的是 kWh，故除以 1000 再乘上小時數；
        // 間隔取自實際相鄰兩點的時間差
        const gapHours = prevTime === null ? 0 : (timestamp - prevTime) / 3600000
        pvAccum += (pv / 1000) * gapHours
        loadAccum += (load / 1000) * gapHours
        prevTime = timestamp

        columns.pvpower.push(round(pv, 0))
        columns.gridpower.push(round(grid, 0))
        columns.loadmeteractivepower.push(round(load, 0))
        columns.batterysoc.push(round(socAt(timestamp), 1))
        // 電池目前未作用，兩者恆為 0
        columns.chargingpower.push(0)
        columns.dischargingpower.push(0)
        columns.todaypvpower.push(round(pvAccum, 2))
        columns.todayloadpower.push(round(loadAccum, 2))
    })

    return columns
}

function round(value, digits) {
    const factor = 10 ** digits
    return Math.round(value * factor) / factor
}
